/**
 * Voice data plane subscriber — the SSE half of the two-channel model.
 *
 * Audio plane (Daily WebRTC, see `daily-session.ts`) carries the
 * visitor↔bot voice. Data plane (this file) carries the bot's tool
 * side-effects — highlight / navigate / open-content / book-meeting —
 * over a long-lived SSE stream from chat-api.
 *
 * The events arrive in the *same* envelope shape as the text path,
 * so the widget's existing `handleChatEvent` dispatcher renders them
 * with no special-casing — voice and text share one rendering path.
 *
 *   GET https://api.showpilot.com/v1/voice/events?sessionId=X&publicId=Y
 *
 * Reconnects with exponential backoff if the stream drops. Subscription
 * is keyed by sessionId — the same id minted by /v1/voice/token and
 * shared with the bot subprocess.
 */

import type { ChatEvent } from '@showpilot/core';

const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 10_000;

export type SubscribeVoiceEventsOptions = {
  chatApiBaseUrl: string;
  sessionId: string;
  publicId: string;
  /** Called for every ChatEvent received. Errors thrown by the handler are caught + logged. */
  onEvent: (event: ChatEvent) => void;
  /** Called once the stream first opens (after the `ready` envelope). */
  onReady?: () => void;
  /** Called when the stream drops (still attempts to reconnect). */
  onDisconnect?: (reason: string) => void;
};

export type VoiceEventSubscription = {
  close(): void;
};

export function subscribeVoiceEvents(
  opts: SubscribeVoiceEventsOptions,
): VoiceEventSubscription {
  let source: EventSource | null = null;
  let closed = false;
  let attempt = 0;

  const url =
    `${opts.chatApiBaseUrl.replace(/\/+$/, '')}/v1/voice/events?` +
    `sessionId=${encodeURIComponent(opts.sessionId)}` +
    `&publicId=${encodeURIComponent(opts.publicId)}`;

  const connect = (): void => {
    if (closed) return;
    source = new EventSource(url, { withCredentials: true });

    source.addEventListener('ready', () => {
      attempt = 0;
      opts.onReady?.();
    });

    // Heartbeats land as named `heartbeat` events with no data — used
    // to keep proxies from idle-killing the connection. We don't need
    // to do anything with them on receipt; the listener just exists so
    // the named event isn't surfaced as an unhandled `message`.
    source.addEventListener('heartbeat', () => {
      /* no-op */
    });

    source.onmessage = (msg) => {
      if (!msg.data) return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(msg.data);
      } catch (err) {
        console.warn('[voice/data-plane] bad event payload', err);
        return;
      }
      try {
        const event = parsed as ChatEvent;
        console.log('[voice/data-plane] received event', event);
        opts.onEvent(event);
      } catch (err) {
        console.warn('[voice/data-plane] event handler threw', err);
      }
    };

    source.onerror = () => {
      // EventSource auto-reconnects, but it doesn't expose the reason
      // and the implicit backoff is unbounded. We take over: close it,
      // log, and reschedule with capped exponential backoff. Once
      // EventSource's readyState is CLOSED the auto-reconnect is off.
      const reason = source?.readyState === EventSource.CLOSED ? 'closed' : 'transient';
      opts.onDisconnect?.(reason);
      source?.close();
      source = null;
      if (closed) return;
      attempt += 1;
      const delay = Math.min(
        RECONNECT_BASE_MS * 2 ** (attempt - 1),
        RECONNECT_MAX_MS,
      );
      setTimeout(() => connect(), delay);
    };
  };

  connect();

  return {
    close(): void {
      closed = true;
      source?.close();
      source = null;
    },
  };
}
